import { useState, useEffect } from 'react'
import {
  ClipboardList,
  Search,
  RefreshCw,
  Filter,
  ChevronDown,
  DoorOpen,
  DoorClosed,
  Car,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { getAccessEvents, getRecentEvents, getDeniedEvents, getLprEvents } from '../lib/api'

interface AccessEvent {
  id: number
  event_type: string
  timestamp: string
  tenant?: string | null
  door?: string | null
  vehicle_plate?: string | null
  summary: string
  description?: string | null
}

type TabType = 'all' | 'denied' | 'lpr'

export default function AccessLogs() {
  const [events, setEvents] = useState<AccessEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [hours, setHours] = useState(24)

  const fetchEvents = async () => {
    setLoading(true)
    try {
      let data: AccessEvent[]
      switch (activeTab) {
        case 'denied':
          data = await getDeniedEvents(hours) as AccessEvent[]
          break
        case 'lpr':
          data = await getLprEvents(hours) as AccessEvent[]
          break
        default:
          data = await getRecentEvents(hours, 100) as AccessEvent[]
      }
      setEvents(data || [])
    } catch (err) {
      console.error('Failed to load events:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [activeTab, hours])

  const getEventIcon = (eventType: string) => {
    if (eventType.includes('denied')) {
      return <XCircle className="w-5 h-5 text-red-500" />
    }
    if (eventType.includes('lpr')) {
      return <Car className="w-5 h-5 text-blue-500" />
    }
    if (eventType.includes('unlock') || eventType.includes('open')) {
      return <DoorOpen className="w-5 h-5 text-green-500" />
    }
    if (eventType.includes('forced') || eventType.includes('alarm')) {
      return <AlertTriangle className="w-5 h-5 text-orange-500" />
    }
    return <DoorClosed className="w-5 h-5 text-gray-500" />
  }

  const getEventBadge = (eventType: string) => {
    const styles: Record<string, string> = {
      door_unlock: 'bg-green-100 text-green-800',
      door_denied: 'bg-red-100 text-red-800',
      gate_open: 'bg-green-100 text-green-800',
      gate_denied: 'bg-red-100 text-red-800',
      lpr_matched: 'bg-blue-100 text-blue-800',
      lpr_detected: 'bg-gray-100 text-gray-800',
      lpr_unknown: 'bg-yellow-100 text-yellow-800',
    }
    return styles[eventType] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Access Logs</h1>
          <p className="text-gray-600 mt-1">View access events and security alerts</p>
        </div>
        <button
          onClick={fetchEvents}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            All Events
          </button>
          <button
            onClick={() => setActiveTab('denied')}
            className={`px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${
              activeTab === 'denied'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Denied Access
          </button>
          <button
            onClick={() => setActiveTab('lpr')}
            className={`px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${
              activeTab === 'lpr'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            LPR Events
          </button>
        </div>

        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white"
          >
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
            <option value={168}>Last 7 days</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No events found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Person/Vehicle
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {getEventIcon(event.event_type)}
                        <span className="text-sm text-gray-900">
                          {event.summary || event.description || event.event_type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventBadge(event.event_type)}`}>
                        {event.event_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {event.door || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {event.tenant || event.vehicle_plate || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(event.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

